import { useQuery } from "@tanstack/react-query";
import { useApi } from "@ncp/api-client";

export type NewsKind = "Intelligence" | "Deal" | "Market" | "Press";

export interface NewsSummary {
  id: string;
  sourceKey: string;
  externalId: string;
  kind: NewsKind;
  headline: string;
  summary?: string | null;
  publishedAt: string;
  url?: string | null;
  companies: string[];
  countries: string[];
  sectors: string[];
  intelligenceType?: string | null;
  sizeBand?: string | null;
  relatedDealId?: string | null;
  language: string;
}

export interface NewsStats {
  total: number;
  today: number;
  lastMonth?: number;
  mergermarket: number;
  marketfeed: number;
  intelligence: number;
  deal: number;
  market: number;
  press: number;
  companies?: number;
}

export interface NewsCompanyCard {
  id: string;
  sourceKey: string;
  externalId: string;
  name: string;
  summary?: string | null;
  url?: string | null;
  companies: string[];
  sectors: string[];
}

export interface NewsView {
  lastSync?: string | null;
  recordCount?: number | null;
  lastMonthFrom?: string | null;
  stats: NewsStats;
  sources: Array<{ key: string; count: number }>;
  kinds: Array<{ key: string; count: number }>;
  briefing?: { companies: NewsCompanyCard[]; lastMonthMergers: NewsSummary[] };
  items: NewsSummary[];
}

export interface NewsDealLink {
  id: string;
  externalId: string;
  title: string;
  status: string;
  filingStatus: string;
  reviewStatus: string;
  score: number;
  nexus: string;
  providerUrl?: string | null;
}

export interface NewsDetail {
  item: NewsSummary;
  body?: string | null;
  rawJson?: string | null;
  deal?: NewsDealLink | null;
}

export interface NewsFilters {
  source?: string;
  kind?: string;
  q?: string;
  country?: string;
  window?: string;
}

export interface NewsSyncResult {
  queued: boolean;
  jobId?: string;
  fetched?: number;
  inserted?: number;
  updated?: number;
  errors?: string[];
}

export const newsKeys = {
  all: ["news"] as const,
  list: (f: NewsFilters) => ["news", "list", f] as const,
  detail: (id: string | null) => ["news", "detail", id] as const,
};

export function useNews(filters: NewsFilters) {
  const api = useApi();
  return useQuery({ queryKey: newsKeys.list(filters), queryFn: () => api.get<NewsView>("/api/news", { ...filters }) });
}

export function useNewsItem(id: string | null) {
  const api = useApi();
  return useQuery({ queryKey: newsKeys.detail(id), queryFn: () => api.get<NewsDetail>(`/api/news/${id}`), enabled: !!id });
}

export const NEWS_SYNCERS = ["HeadOfComplaints", "Director", "Admin", "EconomicResearcher"];

export const SOURCE_KEYS = ["mergermarket", "marketfeed"] as const;
